import React, { memo, useCallback, useMemo } from 'react';
import { styled as styled2 } from '@compiled/react';
import isNil from 'lodash/isNil';
import { token } from '@atlaskit/tokens';
import { PURPLE } from '@atlassian/jira-issue-epic-color-types/src/types.tsx';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { transformToEpic } from '@atlassian/jira-issue-field-parent-switcher/src/common/utils.tsx';
import { ParentSwitcherBoundary } from '@atlassian/jira-issue-field-parent-switcher/src/ui/async.tsx';
import type { ParentSwitcherField as ParentSwitcherFieldType } from '@atlassian/jira-issue-field-parent-switcher/src/ui/main.tsx';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { useShouldRefetchEpicPanel } from '@atlassian/jira-software-backlog-panel/src/services/epic-panel-refetch/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { fg } from '@atlassian/jira-feature-gating';
import { BACKLOG_PACKAGE_NAME } from '../../../../../common/constants/analytics.tsx';
import { useIsCMPBacklog } from '../../../../../state/state-hooks/capabilities.tsx';
import SLAExperienceTracker from '../../../../sla-experience-tracker/index.tsx';
import { CARD_CONTAINER_SELECTOR } from '../../constants.tsx';
import { useBacklogSelector } from '../../../../../state/index.tsx';
import { getConfig } from '../../../../../state/selectors/config/index.tsx';
import ParentFieldStatic from './parent-field-static/index.tsx';
import type { ParentFieldProps, ParentFieldWrapperProps } from './types.tsx';

export const ParentFieldLazy = lazyForPaint<typeof ParentSwitcherFieldType>(() =>
	import(
		/* webpackChunkName: "async-issue-field-parent-switcher" */ '@atlassian/jira-issue-field-parent-switcher'
	).then(({ ParentSwitcherField }) => ParentSwitcherField),
);

const ParentField = ({
	issueId,
	issueKey,
	isUserAtLeastMember,
	parents = [],
	currentProjectId,
	projectIds,
	shouldRenderRichCard,
	forceEditable,
	parentId,
	parent,
	isCompactView,
	workType,
}: ParentFieldProps) => {
	// Remove when cleaning up jsw-backlog-parent-field-render
	const [parentValue] = useFieldValue({
		issueKey,
		fieldKey: PARENT_TYPE,
	});

	const isCompanyManaged = useIsCMPBacklog();
	const levelOneHierarchyName = useBacklogSelector((state) =>
		fg('jsw_backlog_fix_inline_setting_parent_text_issue')
			? getConfig(state)?.hierarchyNomenclature?.firstLevelName
			: undefined,
	);

	const [_, { triggerEpicPanelRefetch }] = useShouldRefetchEpicPanel();

	/**
	 * This outputs the same thing as the parent, it just changes the type to Epic
	 */
	// Remove when cleaning up jsw-backlog-parent-field-render
	// @ts-expect-error Change of type affects transformToEpic type
	const transformedEpic = useMemo(() => parents.map((p) => transformToEpic(p)), [parents]);

	// Mapped parent is required to get the epic/parent's color
	// Remove when cleaning up jsw-backlog-parent-field-render
	const mappedParent = useMemo(
		() => (parentId ? parents.find(({ id }) => Number(id) === Number(parentId)) : null),
		[parentId, parents],
	);

	// Remove when cleaning up jsw-backlog-parent-field-render
	const renderParentFieldStatic = useCallback(() => {
		const color = mappedParent?.color || parentValue?.color || PURPLE;
		const summary = mappedParent?.summary || parentValue?.fields?.summary;

		return summary ? (
			<ParentFieldStatic color={color} summary={summary} isCompactView={isCompactView} />
		) : null;
	}, [isCompactView, mappedParent, parentValue?.color, parentValue?.fields?.summary]);

	const color = fg('jsw-backlog-parent-field-render')
		? parent?.color || mappedParent?.color || parentValue?.color || PURPLE
		: null;
	const summary = fg('jsw-backlog-parent-field-render')
		? parent?.summary || mappedParent?.summary || parentValue?.fields?.summary
		: null;

	const parentFieldStatic =
		summary && fg('jsw-backlog-parent-field-render') ? (
			<ParentFieldStatic color={color} summary={summary} isCompactView={isCompactView} />
		) : null;

	const handleSuccess = useCallback(() => {
		if (isCompanyManaged) {
			triggerEpicPanelRefetch();
		}
	}, [isCompanyManaged, triggerEpicPanelRefetch]);

	// in the storybook, it first displays the static epic lozenge.
	// but when we mouse over on a card, the epic field would disappear due to missing hierarchy-level-above value in the sweet state
	if (isUserAtLeastMember && shouldRenderRichCard) {
		return (
			<ParentSwitcherBoundary
				packageName={BACKLOG_PACKAGE_NAME}
				fallback={
					fg('jsw-backlog-parent-field-render') ? parentFieldStatic : renderParentFieldStatic()
				}
			>
				<SLAExperienceTracker
					experienceTarget="inline epic"
					experienceTargetId={issueKey}
					additionalAttributes={{ issueKey }}
				>
					<ParentFieldWrapper
						hasValue={fg('jsw-backlog-parent-field-render') ? !!parent : !isNil(parentValue)}
						isCompactView={isCompactView}
					>
						<ContextualAnalyticsData attributes={{ isInlineEditing: true }}>
							<ParentFieldLazy
								onSuccess={handleSuccess}
								issueKey={issueKey}
								issueId={String(issueId)}
								epics={fg('jsw-backlog-parent-field-render') ? parents : transformedEpic}
								levelOneHierarchyName={levelOneHierarchyName}
								/* We cannot get permission when no issue view is opened by using the useProjectPermission hook in @atlassian/jira-project-permissions-service
								 * So we have to pass canUserEditIssue from the backlog to the extracted epic field to force it to be editable
								 * */
								forceEditable={forceEditable}
								/** Same reason as above, we need the all bellow in order to
								 *  open the issue parent switcher modal */
								isSimplifiedProject={!isCompanyManaged}
								currentProjectId={currentProjectId}
								projectIds={projectIds}
								isCompactView={isCompactView}
								workType={workType}
							/>
						</ContextualAnalyticsData>
					</ParentFieldWrapper>
				</SLAExperienceTracker>
			</ParentSwitcherBoundary>
		);
	}

	return fg('jsw-backlog-parent-field-render') ? parentFieldStatic : renderParentFieldStatic();
};

export default memo<ParentFieldProps>(ParentField);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const ParentFieldWrapper = styled2.div<ParentFieldWrapperProps>({
	display: 'flex',
	alignItems: 'flex-end',
	position: 'relative',
	cursor: 'default',
	maxWidth: '100%',
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > div + div': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		position: 'absolute !important',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		top: `${token('space.400')} !important`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		right: 'auto !important',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		left: `${token('space.050')} !important`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		transform: 'none !important',
	},
	// Only add padding on large screens
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingLeft: ({ isCompactView }) => (!isCompactView ? token('space.100') : undefined),
	/*
	 * NOTE: Only show epic/parent field if the card is hovered, focused, or has
	 * a value display: none OR visibility: hidden can't be used here as it
	 * messes up focus state when accessed via keyboard (Tab and Enter)
	 */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: ({ hasValue }) => (!hasValue ? 0 : undefined),

	/* undo hide if card is focused or hovered */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:hover &, ` +
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:active &, ` +
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:focus &, ` +
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:focus-within &`]: {
		opacity: 'inherit',
	},
});
